import * as RadixSwitch from "@radix-ui/react-switch";
import { forwardRef, useId } from "react";
import styled from "@emotion/styled";
import { twRing, twTransform } from "@/lib/emotionTw";

export type SwitchProps = RadixSwitch.SwitchProps & {
  label?: string;
};

interface SwitchOwnerState {
  disabled: boolean;
}

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline. */

const SwitchRoot = styled("div", {
  label: "AgenticSwitch-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: SwitchOwnerState }>(({ ownerState }) => [
  {
    display: "inline-flex",
    alignItems: "center",
    gap: ".5rem",
    fontSize: ".875rem",
    lineHeight: "1.25rem",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.disabled && {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
  }
]);

const SwitchControl = styled(RadixSwitch.Root, {
  label: "AgenticSwitch-control"
})({
  position: "relative",
  height: "1.5rem",
  width: "2.75rem",
  cursor: "pointer",
  borderRadius: "var(--bfrs-radius-pill)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-border-strong) / var(--tw-bg-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:focus-visible": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .25)"),
  "&:disabled": {
    cursor: "not-allowed",
    opacity: ".5"
  },
  '&[data-state="checked"]': {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))"
  }
});

const SwitchThumb = styled(RadixSwitch.Thumb, {
  label: "AgenticSwitch-thumb"
})({
  display: "block",
  width: "1.25rem",
  height: "1.25rem",
  ...twTransform({ "--tw-translate-x": ".125rem" }),
  borderRadius: "var(--bfrs-radius-pill)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  "--tw-shadow": "var(--bfrs-shadow-low)",
  "--tw-shadow-colored": "var(--bfrs-shadow-low)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
  transitionProperty: "transform",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  '&[data-state="checked"]': {
    "--tw-translate-x": "22px",
    transform:
      "translate(var(--tw-translate-x),var(--tw-translate-y)) rotate(var(--tw-rotate)) skew(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
  }
});

const SwitchLabel = styled("label", {
  label: "AgenticSwitch-label",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: SwitchOwnerState }>(({ ownerState }) =>
  ownerState.disabled ? { cursor: "not-allowed" } : { cursor: "pointer" }
);

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(({ className, label, ...props }, ref) => {
  const generatedId = useId();
  const switchId = (props.id as string | undefined) ?? generatedId;
  const ownerState: SwitchOwnerState = { disabled: Boolean(props.disabled) };

  return (
    <SwitchRoot ownerState={ownerState}>
      <SwitchControl ref={ref} id={switchId} className={className} {...props}>
        <SwitchThumb />
      </SwitchControl>
      {label && (
        <SwitchLabel htmlFor={switchId} ownerState={ownerState}>
          {label}
        </SwitchLabel>
      )}
    </SwitchRoot>
  );
});

Switch.displayName = "Switch";
